import { promotionCalendarDateKey } from "../../../constants/promotionEvents";
import { hasPromotionEventElapsed } from "../../../constants/promotionStatuses";

export const HISTORY_FIELD_LIST = Object.freeze([
  { name: "name", label: "Promotion Name" },
  { name: "book", label: "Book Name" },
  { name: "createdBy", label: "Created By" },
  { name: "createdOn", label: "Created On" },
  { name: "eventDate", label: "Promotion Date" },
  { name: "eventTime", label: "Promotion Time" },
  { name: "venue", label: "Promotion Venue" },
  { name: "status", label: "Promotion Status" },
]);

export const DEFAULT_HISTORY_FIELD_CHECK = Object.freeze({
  name: true,
  book: true,
  createdBy: true,
  createdOn: false,
  eventDate: true,
  eventTime: false,
  venue: true,
  status: true,
});

export const HISTORY_EMPTY_FIELDS_COPY = "Select fields to view data";

export const isHistoryFieldVisibleOnTab = (fieldName, tab) => {
  if (fieldName === "status") return tab === "CLOSED";
  return true;
};

export const visibleHistoryDataColumns = (check = {}, tab) =>
  HISTORY_FIELD_LIST.filter(
    (field) =>
      Boolean(check[field.name]) && isHistoryFieldVisibleOnTab(field.name, tab)
  );

export const visibleHistoryColSpan = (check, tab) =>
  visibleHistoryDataColumns(check, tab).length + 1;

export const allHistoryDataFieldsHidden = (check, tab) =>
  visibleHistoryDataColumns(check, tab).length === 0;

/** UPCOMING while the event is still ahead, ACTION_NEEDED once it has elapsed. */
export const getInviteSentAttention = (
  eventDate,
  now = new Date(),
  { hasEventTime = true } = {}
) =>
  hasPromotionEventElapsed(eventDate, now, { hasEventTime })
    ? "ACTION_NEEDED"
    : "UPCOMING";

export const HISTORY_DATE_FILTER_FIELDS = Object.freeze({
  EVENT_DATE: "eventDate",
  CREATED_AT: "createdAt",
  COMPLETED_ON: "completedOn",
});

export const HISTORY_DATE_FILTER_OPTIONS = Object.freeze([
  { value: HISTORY_DATE_FILTER_FIELDS.EVENT_DATE, label: "Event Date" },
  { value: HISTORY_DATE_FILTER_FIELDS.CREATED_AT, label: "Created On" },
]);

export const PROMOTIONS_DATE_FILTER_OPTIONS = Object.freeze([
  {
    value: HISTORY_DATE_FILTER_FIELDS.COMPLETED_ON,
    label: "Completed On",
  },
]);

export const DEFAULT_HISTORY_DATE_FILTER_FIELD =
  HISTORY_DATE_FILTER_FIELDS.EVENT_DATE;

export const DEFAULT_PROMOTIONS_DATE_FILTER_FIELD =
  HISTORY_DATE_FILTER_FIELDS.COMPLETED_ON;

export const isPastOnlyDateFilterField = (field) =>
  field === HISTORY_DATE_FILTER_FIELDS.CREATED_AT ||
  field === HISTORY_DATE_FILTER_FIELDS.COMPLETED_ON;

const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;

export const toCalendarDay = (value) => {
  if (!value) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (CALENDAR_DAY.test(trimmed)) return trimmed;
  }
  if (typeof value.format === "function" && value.isValid?.()) {
    return value.format("YYYY-MM-DD");
  }
  return promotionCalendarDateKey(value) || undefined;
};

export const todayCalendarDay = (now = new Date()) => toCalendarDay(now);

export const clampCreatedAtCalendarDay = (ymd, today = todayCalendarDay()) => {
  if (!ymd) return undefined;
  return ymd > today ? today : ymd;
};

export const shiftCalendarDay = (ymd, days) => {
  const day = toCalendarDay(ymd);
  if (!day) return undefined;
  const [year, month, date] = day.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, date + days));
  if (Number.isNaN(shifted.getTime())) return undefined;
  return shifted.toISOString().slice(0, 10);
};

export const resolveHistoryDateRange = ({
  startDate,
  endDate,
  today = todayCalendarDay(),
} = {}) => {
  const start = toCalendarDay(startDate);
  const end = toCalendarDay(endDate);

  if (start && end) {
    if (end < start) return null;
    return { startDate: start, endDate: end };
  }

  if (start && !end) {
    if (start > today) return null;
    return { startDate: start, endDate: today };
  }

  if (end && !start) {
    const inferredStart = shiftCalendarDay(end, -7);
    if (!inferredStart || inferredStart > end) return null;
    return { startDate: inferredStart, endDate: end };
  }

  return null;
};

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const formatHistoryPickerDay = (ymd) => {
  const day = toCalendarDay(ymd);
  if (!day) return "";
  const [year, month, date] = day.split("-").map(Number);
  if (!year || !month || !date) return "";
  const dd = String(date).padStart(2, "0");
  const mon = MONTH_SHORT[month - 1];
  const yy = String(year).slice(-2);
  if (!mon) return "";
  return `${dd} ${mon} ${yy}`;
};

export const formatHistoryDateRangeLabel = (startDate, endDate) => {
  const start = formatHistoryPickerDay(startDate);
  const end = formatHistoryPickerDay(endDate);
  if (!start || !end) return "";
  return `${start} - ${end}`;
};

export const canApplyHistoryDateRange = (range, today) =>
  Boolean(resolveHistoryDateRange({ ...range, today }));

export const buildHistoryDateQueryArgs = ({
  field = DEFAULT_HISTORY_DATE_FILTER_FIELD,
  startDate,
  endDate,
  today = todayCalendarDay(),
} = {}) => {
  let start = toCalendarDay(startDate);
  let end = toCalendarDay(endDate);
  if (!start || !end || end < start) return {};

  if (isPastOnlyDateFilterField(field)) {
    start = clampCreatedAtCalendarDay(start, today);
    end = clampCreatedAtCalendarDay(end, today);
    if (!start || !end || end < start) return {};
  }

  if (field === HISTORY_DATE_FILTER_FIELDS.CREATED_AT) {
    return {
      createdStartDate: start,
      createdEndDate: end,
    };
  }

  if (field === HISTORY_DATE_FILTER_FIELDS.COMPLETED_ON) {
    return {
      startDate: start,
      endDate: end,
    };
  }

  return {
    eventStartDate: start,
    eventEndDate: end,
  };
};

export const HISTORY_LIST_TABS = Object.freeze([
  { value: "PROMOTION_CREATED", label: "Promotion Created" },
  { value: "INVITE_SENT", label: "Invite Sent" },
  { value: "ACTION_NEEDED", label: "Action Needed" },
  { value: "CLOSED", label: "Closed" },
]);

const HISTORY_LIST_TAB_VALUES = new Set(
  HISTORY_LIST_TABS.map((item) => item.value)
);

export const historyListTabForStatus = (status) => {
  if (status === "INVITE_SENT") return "INVITE_SENT";
  if (status === "ACTION_NEEDED") return "ACTION_NEEDED";
  if (status === "COMPLETED" || status === "CLOSED") return "CLOSED";
  return "PROMOTION_CREATED";
};

/** Invite Sent events past their time belong on the Action Needed tab. */
export const historyListTabForInvite = (invite, now = new Date()) => {
  if (invite?.status === "INVITE_SENT") {
    return getInviteSentAttention(invite.eventDate, now, {
      hasEventTime: invite.hasEventTime !== false,
    }) === "ACTION_NEEDED"
      ? "ACTION_NEEDED"
      : "INVITE_SENT";
  }
  return historyListTabForStatus(invite?.status);
};

export const historyTabLabel = (statusOrInvite, now = new Date()) => {
  const tabValue =
    typeof statusOrInvite === "string" || statusOrInvite == null
      ? historyListTabForStatus(statusOrInvite)
      : historyListTabForInvite(statusOrInvite, now);
  const tab = HISTORY_LIST_TABS.find((item) => item.value === tabValue);
  return tab?.label || "Promotion Created";
};

const splitCsvParam = (value) =>
  String(value || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

const appendIdParam = (params, key, ids, single) => {
  const list = (ids || []).map(String).filter(Boolean);
  if (!list.length && single) list.push(String(single));
  if (list.length) params.set(key, list.join(","));
};

export const readHistoryListFilters = (searchParams) => {
  const tab = searchParams.get("tab");
  const dateField = searchParams.get("dateField");
  const start = toCalendarDay(searchParams.get("start"));
  const end = toCalendarDay(searchParams.get("end"));
  const hasRange = Boolean(start && end && end >= start);
  const companyIds = splitCsvParam(searchParams.get("company"));
  const bookIds = splitCsvParam(searchParams.get("book"));
  return {
    tab: HISTORY_LIST_TAB_VALUES.has(tab) ? tab : "PROMOTION_CREATED",
    search: searchParams.get("q") || "",
    companyIds,
    bookIds,
    dateField:
      dateField === HISTORY_DATE_FILTER_FIELDS.CREATED_AT
        ? dateField
        : DEFAULT_HISTORY_DATE_FILTER_FIELD,
    dateStart: hasRange ? start : null,
    dateEnd: hasRange ? end : null,
  };
};

export const writeHistoryListSearch = ({
  tab,
  search,
  companyIds,
  companyId,
  bookIds,
  bookId,
  dateField,
  dateStart,
  dateEnd,
} = {}) => {
  const params = new URLSearchParams();
  if (tab && tab !== "PROMOTION_CREATED") params.set("tab", tab);
  if (search) params.set("q", search);
  appendIdParam(params, "company", companyIds, companyId);
  appendIdParam(params, "book", bookIds, bookId);
  const start = toCalendarDay(dateStart);
  const end = toCalendarDay(dateEnd);
  if (start && end && end >= start) {
    if (dateField && dateField !== DEFAULT_HISTORY_DATE_FILTER_FIELD) {
      params.set("dateField", dateField);
    }
    params.set("start", start);
    params.set("end", end);
  }
  const query = params.toString();
  return query ? `?${query}` : "";
};
